import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { SortDescriptor, orderBy, CompositeFilterDescriptor, filterBy } from "@progress/kendo-data-query";
import styles from "./ProductPricingModels.module.css";
import PricingModelInstructions from "src/components/PricingModelInstructions";

interface PricingModel {
  modelId: number;
  acctId: string;
  productName: string;
  skuId: number | null;
  providerName: string;
}

interface ProviderOption {
  providerId: number;
  providerName: string;
}

const PAGE_SIZES = [10, 25, 50, 100, 250];
const PAGE_SIZE_KEY = "ppm-pageSize";
const API_BASE = "sysadmin/product-pricing-models";

const ProductPricingModels: React.FC = () => {
  const navigate = useNavigate();
  const savedPageSize = Number(localStorage.getItem(PAGE_SIZE_KEY)) || 25;
  const [data, setData] = useState<PricingModel[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState({ skip: 0, take: savedPageSize });
  const [sort, setSort] = useState<SortDescriptor[]>([]);
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);

  // Add Model modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [newAcctId, setNewAcctId] = useState("");
  const [newProviderId, setNewProviderId] = useState<number | "">("");  const [newSkuId, setNewSkuId] = useState("");  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const result = await callApi(API_BASE, HttpMethod.GET);
      setData(result);
    } catch {
      toast.error("Failed to load pricing models.");
    } finally {
      setLoaded(true);
    }
  };

  const syncPageSize = (size: number) => {
    localStorage.setItem(PAGE_SIZE_KEY, String(size));
  };

  const openAddModal = async () => {
    if (providers.length === 0) {
      try {
        const result = await callApi(`${API_BASE}/providers`, HttpMethod.GET);
        setProviders(result);
      } catch {
        toast.error("Failed to load providers.");
        return;
      }
    }
    setNewAcctId("");
    setNewProviderId("");
    setNewSkuId("");
    setShowAddModal(true);
  };

  const submitAddModel = async () => {
    if (!newProviderId) {
      toast.error("Provider is required.");
      return;
    }
    if (!/^[1-9]\d*$/.test(newSkuId.trim())) {
      toast.error("SKU ID is required and must be a positive integer.");
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await callApi(API_BASE, HttpMethod.POST, { acctId: newAcctId.trim(), providerId: newProviderId, skuId: Number(newSkuId) });
      if (result.type === "error") {
        toast.error(result.message);
      } else {
        toast.success(result.message || "Model created.");
        setShowAddModal(false);
        navigate(`/sysadmin/product-pricing-models/${result.modelId}`);
      }
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || "Failed to create model.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteModel = async (item: PricingModel) => {
    if (!window.confirm(`Delete model id:${item.modelId} for ${item.acctId} - ${item.providerName}?`)) return;
    try {
      await callApi(`${API_BASE}/${item.modelId}`, HttpMethod.DELETE);
      setData((prev) => prev.filter((m) => m.modelId !== item.modelId));
      toast.success("Model deleted.");
    } catch {
      toast.error("Failed to delete model.");
    }
  };

  const CommandCell = useCallback((props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => navigate(`/sysadmin/product-pricing-models/${dataItem.modelId}`)}
          >
            Edit
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => deleteModel(dataItem)}
          >
            Delete
          </button>
        </div>
      </td>
    );
  }, []);

  const filtered = useMemo(
    () => filter ? filterBy(orderBy(data, sort), filter) : orderBy(data, sort),
    [data, sort, filter],
  );

  const paged = useMemo(
    () => filtered.slice(page.skip, page.skip + page.take),
    [filtered, page],
  );

  return (
    <div className={styles.container}>
      {loaded && (
        <div>
          <div className="mb-2 d-flex gap-2">
            <PricingModelInstructions />
            <button className="btn btn-success btn-sm" onClick={openAddModal}>
              + Add Model
            </button>
          </div>
          <Grid
            className="sysadmin-grid"
            data={paged}
            total={filtered.length}
            skip={page.skip}
            take={page.take}
            dataItemKey="modelId"
            sortable={true}
            resizable={true}
            sort={sort}
            onSortChange={(e: GridSortChangeEvent) => { setSort(e.sort); setPage((p) => ({ ...p, skip: 0 })); }}
            filterable={true}
            filter={filter}
            onFilterChange={(e: GridFilterChangeEvent) => { setFilter(e.filter); setPage((p) => ({ ...p, skip: 0 })); }}
            pageable={{ pageSizes: PAGE_SIZES }}
            onPageChange={(e: GridPageChangeEvent) => { syncPageSize(e.page.take); setPage({ skip: e.page.skip, take: e.page.take }); }}
            style={{ height: "calc(100vh - 125px)", width: "100%" }}
          >
            <Column field="modelId" title="Model ID" width={150} filter="text" />
            <Column field="acctId" title="Account ID" width={150} filter="text" />
            <Column field="productName" title="Product" width={450} filter="text" />
            <Column field="providerName" title="Provider" width={500} filter="text" />
            <Column field="skuId" title="SKU ID" width={150} filter="text" />
            <Column title="Actions" width={180} filterable={false} sortable={false} cells={{ data: CommandCell }} />
          </Grid>
        </div>
      )}

      {showAddModal && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Model</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Account ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newAcctId}
                    onChange={(e) => setNewAcctId(e.target.value)}
                    placeholder="Enter account ID"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Provider</label>
                  <select
                    className="form-select"
                    value={newProviderId}
                    onChange={(e) => setNewProviderId(Number(e.target.value) || "")}
                  >
                    <option value="">-- Select provider --</option>
                    {providers.map((p) => (
                      <option key={p.providerId} value={p.providerId}>{p.providerName}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">SKU ID</label>
                  <input
                    type="text"
                    className={`form-control${newSkuId && !/^[1-9]\d*$/.test(newSkuId.trim()) ? " is-invalid" : ""}`}
                    value={newSkuId}
                    onChange={(e) => setNewSkuId(e.target.value)}
                    placeholder="Enter SKU ID"
                  />
                  {newSkuId && !/^[1-9]\d*$/.test(newSkuId.trim()) && (
                    <div className="invalid-feedback">Required, positive integer</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={submitAddModel} disabled={isSubmitting}>
                  {isSubmitting ? "Adding…" : "Add"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPricingModels;
